import { CompensationRollupValue } from "@/components/sensitive-value/CompensationRollupValue.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  ChangesPreview,
  ChangesPreviewItem,
} from "@/pages/employees/employee/components/ChangesPreview.tsx";
import {
  CompensationLineChange,
  CompensationLineDiff,
} from "@/pages/employees/employee/widgets/compensation/compensation-package-diff.ts";
import { ApiCompensationComponent } from "@/types/compensation-component.types.ts";
import {
  CompensationLineSave,
  CompensationPreview,
  getEmployeeCompensationPerTypeTitle,
} from "@/types/employees/employee-compensation.types.ts";
import { Box, ListItem, Stack, Text, Title } from "@skailer-com/uikit";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface EmployeeCompensationPreviewProps {
  changeItems: ChangesPreviewItem[];
  components: ApiCompensationComponent[];
  lines: CompensationLineSave[];
  diffs: CompensationLineDiff[];
  preview: CompensationPreview;
  missingLineTitles: string[];
  hasCompensationLines: boolean;
}

interface CompensationPreviewCardProps {
  title: string;
  period: string;
  employeeReceives: ReactNode;
  companySpends: ReactNode;
  change?: CompensationLineChange;
  isTotal?: boolean;
}

function CompensationPreviewCard({
  title,
  period,
  employeeReceives,
  companySpends,
  change,
  isTotal = false,
}: CompensationPreviewCardProps) {
  const { t } = useTranslation();
  const formattedPeriod = period.charAt(0).toLocaleUpperCase() + period.slice(1);
  const changeLabel = change ? t(`core:compensation_line_change_${change}`) : null;
  const changeClassName =
    change === "added" ? "text-positive" : change === "removed" ? "text-danger" : "text-warning";

  return (
    <Box variant="border" className="bg-dark/5 p-4">
      <Stack gap="sm">
        <Stack direction={"horizontal"} justify={"between"}>
          <Title header={title} caption={formattedPeriod} size={isTotal ? "md" : "xs"} />
          {changeLabel && <Text className={`font-semibold ${changeClassName}`}>{changeLabel}</Text>}
        </Stack>

        <Stack direction="horizontal" gap="md">
          <ListItem
            className="border-0 w-full"
            titleSlot={<Text className="font-semibold">{employeeReceives}</Text>}
            caption={t("core:compensation_line_net")}
          />
          <ListItem
            className="border-0 w-full"
            titleSlot={<Text className="font-semibold">{companySpends}</Text>}
            caption={t(
              isTotal ? "core:compensation_total_cost_rollup" : "core:compensation_line_gross"
            )}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

export function EmployeeCompensationPreview({
  changeItems,
  components,
  lines,
  diffs,
  preview,
  missingLineTitles,
  hasCompensationLines,
}: EmployeeCompensationPreviewProps) {
  const { t } = useTranslation();
  const { renderCurrency } = useFormatter();
  const periodChanges = changeItems.filter(
    (item) => item.id === "date_effective_from" || item.id === "currency"
  );
  const descriptionChanges = changeItems.filter(
    (item) => item.id !== "date_effective_from" && item.id !== "currency"
  );
  const lineChanges = [
    ...lines.map((line) => ({
      line,
      diff: diffs.find(
        (item) =>
          item.line === line ||
          (line.id !== undefined && item.line.id !== undefined && item.line.id === line.id)
      ),
    })),
    ...diffs.filter((diff) => diff.change === "removed").map((diff) => ({ line: diff.line, diff })),
  ];
  const renderAmount = (amount: number | null, currency: string | null) =>
    amount === null ? "-" : renderCurrency(String(amount), currency);
  const renderRollup = (amount: string | null) => (
    <CompensationRollupValue
      alwaysVisible
      value={amount === null ? null : renderCurrency(amount, preview.rollups.currency_code ?? null)}
      isIncomplete={preview.is_rollup_incomplete}
      isEstimated={preview.is_rollup_estimated}
      missingLineTitles={missingLineTitles}
    />
  );

  return (
    <Stack>
      <ChangesPreview items={periodChanges} />
      {hasCompensationLines && (
        <>
          <Title header={t("core:compensation_package_lines")} size="xs" paddingTop paddingBottom />
          <Stack gap="sm">
            {lineChanges.map(({ line, diff }, index) => {
              const component = components.find(
                (item) => item.id === line.compensation_component_id
              );
              return (
                <CompensationPreviewCard
                  key={line.id ?? `line-${index}`}
                  title={component?.title ?? t("core:compensation_line_component")}
                  period={line.per_type ? getEmployeeCompensationPerTypeTitle(line.per_type) : "-"}
                  employeeReceives={renderAmount(line.amount_net, line.currency_code)}
                  companySpends={renderAmount(line.amount_gross, line.currency_code)}
                  change={diff?.change}
                />
              );
            })}
            <CompensationPreviewCard
              title={t("core:compensation_totals")}
              period={getEmployeeCompensationPerTypeTitle(preview.rollups.per_type)}
              employeeReceives={renderRollup(preview.rollups.net_actual_amount)}
              companySpends={renderRollup(preview.rollups.total_cost_actual_amount)}
              isTotal={true}
            />
          </Stack>
        </>
      )}
      {descriptionChanges.length > 0 && (
        <>
          <Title header={t("common:description")} size="xs" paddingTop />
          <ChangesPreview items={descriptionChanges} />
        </>
      )}
    </Stack>
  );
}
